/**
 * Copy Button Component
 * 
 * Button that copies text to clipboard with visual feedback.
 */

import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { copyToClipboard, copyShareLink } from '../../../utils/clipboard';
import { announce } from '../../../utils/accessibility';
import './CopyButton.css';

/**
 * Copy Button Props
 * @param {string} text - Text to copy
 * @param {string} label - Button label (optional)
 * @param {boolean} showLabel - Whether to show label text
 * @param {string} successMessage - Message on successful copy
 * @param {string} size - Button size ('small', 'medium', 'large')
 * @param {string} variant - Button variant ('default', 'icon-only', 'text')
 * @param {function} onCopy - Callback after copy
 */
export function CopyButton({
  text,
  label = 'Copy',
  showLabel = true,
  successMessage = 'Copied!',
  size = 'medium',
  variant = 'default',
  onCopy,
  className = '',
  ...props
}) {
  const [status, setStatus] = useState('idle'); // idle, success, error

  const handleCopy = useCallback(async () => {
    try {
      const success = await copyToClipboard(text);
      
      if (success) {
        setStatus('success');
        announce(successMessage, 'polite');
        onCopy?.({ success: true, text });
        
        setTimeout(() => setStatus('idle'), 2000);
      } else {
        setStatus('error');
        announce('Copy failed', 'assertive');
        setTimeout(() => setStatus('idle'), 2000);
      }
    } catch (err) {
      setStatus('error');
      announce('Copy failed', 'assertive');
      setTimeout(() => setStatus('idle'), 2000);
    }
  }, [text, successMessage, onCopy]);

  const icon = status === 'success' 
    ? faCheck 
    : status === 'error' 
      ? faExclamationTriangle 
      : faCopy;

  const buttonLabel = status === 'success' 
    ? successMessage 
    : status === 'error' 
      ? 'Failed' 
      : label;

  return (
    <button
      type="button"
      className={`copy-button copy-button-${size} copy-button-${variant} copy-button-${status} ${className}`}
      onClick={handleCopy}
      aria-label={`Copy ${label} to clipboard`}
      {...props}
    >
      <FontAwesomeIcon icon={icon} className="copy-button-icon" />
      {showLabel && variant !== 'icon-only' && (
        <span className="copy-button-label">{buttonLabel}</span>
      )}
    </button>
  );
}

/**
 * Share Link Button - Copies a shareable URL
 */
export function ShareLinkButton({
  path,
  params = {},
  label = 'Share',
  successMessage = 'Link copied!',
  onShare,
  ...props
}) {
  const [status, setStatus] = useState('idle');

  const handleShare = useCallback(async () => {
    try {
      const result = await copyShareLink(path, { params });
      
      if (result.success) {
        setStatus('success');
        announce(successMessage, 'polite');
        onShare?.(result);
        
        setTimeout(() => setStatus('idle'), 2000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 2000);
      }
    } catch (err) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
    }
  }, [path, params, successMessage, onShare]);

  const icon = status === 'success' ? faCheck : faCopy;

  return (
    <button
      type="button"
      className={`copy-button copy-button-share copy-button-${status}`}
      onClick={handleShare}
      aria-label="Copy share link"
      {...props}
    >
      <FontAwesomeIcon icon={icon} className="copy-button-icon" />
      <span className="copy-button-label">
        {status === 'success' ? successMessage : label}
      </span>
    </button>
  );
}

export default CopyButton;
